import {
  BrowserClient,
  defaultStackParser,
  getDefaultIntegrations,
  makeFetchTransport,
  Scope,
  type EventHint,
} from '@sentry/browser'
import { environment } from './environment'

// prevent snippet sentry to conflict with sentry on hosted website.
// we should export things like captureException from this client instead of using the one
// from sentry directly.
let captureExceptionImpl: (exception: unknown, hint?: EventHint) => string = () => ''

export const captureException = (exception: unknown, hint?: EventHint): string =>
  captureExceptionImpl(exception, hint)

export const initSentry = (): void => {
  const client = new BrowserClient({
    dsn: process.env.SENTRY_DSN,
    // Snippet runs on customer pages; environment.baseUrl is derived from the snippet's
    // own <script src> and resolves to the tenant subdomain that hosts /api/sentry-tunnel.
    tunnel: `${environment.baseUrl}/api/sentry-tunnel`,
    release: process.env.SENTRY_RELEASE_COMMIT,
    environment: environment.domain,
    // remove integrations that use global sentry object https://docs.sentry.io/platforms/javascript/best-practices/multiple-sentry-instances/
    integrations: getDefaultIntegrations({}).filter(defaultIntegration => {
      return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
        defaultIntegration.name
      )
    }),
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    beforeSend(event) {
      const eventFrames = event.exception?.values?.flatMap(
        exception => exception.stacktrace?.frames ?? []
      )

      // ignore root-level errors as they are not related to snippet
      if (!eventFrames?.length) {
        return null
      }

      // ignores anything that is not happening in our code
      if (
        !eventFrames.every(
          eventFrame =>
            eventFrame.filename?.includes('/web-data/deanonymization/embed/') ||
            eventFrame.filename?.includes('/web-data/tracking/')
        )
      ) {
        return null
      }

      return event
    },
  })

  const scope = new Scope()
  scope.setClient(client)
  client.init()

  captureExceptionImpl = scope.captureException.bind(scope)
}
